import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { EditorMode, MarkDownEditorCtrl } from '@ingenico/react-ui/mark-down-editor';
import { isEmpty, isUndefined, toArray, toString } from 'lodash-es';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DeviceAppVersionCard } from '../device-app-version-card/DeviceAppVersionCard.ctrl';

import { getSumOfFileSizes } from '../application-version-fields/applicationVersionFields.util';
import { DeviceAppVersionSummaryFileListCard } from '../device-app-version-summary-file-list-card/DeviceAppVersionSummaryFileListCard.ctrl';
import type { SystemPackageVersionSummaryStepParams } from './systemPackageVersionSummaryStep.model';
import styles from './systemPackageVersionSummaryStep.module.scss';
import {
  getDisplayedAutocompleteFieldValue,
  getSelectetInputDisplyedValuesInline,
} from './systemPackageVersionSummaryStep.util';
import { useSystemPackageVersionSummaryStep } from './useSystemPackageVersionSummaryStep.vm';

export function SystemPackageVersionSummaryStep({
  data,
  applicationFiles,
  existingTemplates,
  operationalSystemDependencyOptions,
  appDependencyOptions,
  systemPackageDependencyOptions,
  isLoadingTemplateResources,
}: SystemPackageVersionSummaryStepParams): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.APPLICATION, ALL_LNGS_NAMESPACES.COMMON]);
  const {
    shouldDisplayConfigurationCard,
    parameterTemplatesList,
    existingAppFilesList,
    existingTemplatesList,
    applicationFilesList,
  } = useSystemPackageVersionSummaryStep({ data, applicationFiles, existingTemplates, t });

  return (
    <div className={styles.container}>
      <DeviceAppVersionCard title={t('title.deploymentInfoSection')}>
        <div className={styles.fieldswrapper}>
          <div>
            <CoreTypography variant={CoreTypographyVariant.Body02Medium}>{t('input.version.label')}</CoreTypography>
            <CoreTypography variant={CoreTypographyVariant.Body02Regular}>
              {toString(data?.definition?.version)}
            </CoreTypography>
          </div>
          <div>
            <CoreTypography variant={CoreTypographyVariant.Body02Medium}>
              {t('input.operationalSystemDependency.label')}
            </CoreTypography>
            <CoreTypography variant={CoreTypographyVariant.Body02Regular}>
              {getDisplayedAutocompleteFieldValue(
                operationalSystemDependencyOptions,
                data?.definition?.operationalSystemDependency,
              )}
            </CoreTypography>
            <CoreTypography variant={CoreTypographyVariant.Body02Regular}>
              {getSelectetInputDisplyedValuesInline(data?.definition?.minimumOSVersion)}
            </CoreTypography>
          </div>
          {!isEmpty(data?.definition?.systemPackageDependency) && (
            <div>
              <CoreTypography variant={CoreTypographyVariant.Body02Medium}>
                {t('input.systemPackageDependency.label')}
              </CoreTypography>
              <CoreTypography variant={CoreTypographyVariant.Body02Regular}>
                {getDisplayedAutocompleteFieldValue(
                  systemPackageDependencyOptions,
                  data?.definition?.systemPackageDependency,
                )}
              </CoreTypography>
              <CoreTypography variant={CoreTypographyVariant.Body02Regular}>
                {getSelectetInputDisplyedValuesInline(data?.definition?.minimumSystemPackageVersion)}
              </CoreTypography>
            </div>
          )}
          {!isEmpty(data?.definition?.appDependency) && (
            <div>
              <CoreTypography variant={CoreTypographyVariant.Body02Medium}>
                {t('input.appDependency.label')}
              </CoreTypography>
              <CoreTypography variant={CoreTypographyVariant.Body02Regular}>
                {getDisplayedAutocompleteFieldValue(appDependencyOptions, data?.definition?.appDependency)}
              </CoreTypography>
              <CoreTypography variant={CoreTypographyVariant.Body02Regular}>
                {getSelectetInputDisplyedValuesInline(data?.definition?.minimumAppVersion)}
              </CoreTypography>
            </div>
          )}
        </div>
      </DeviceAppVersionCard>
      {!isUndefined(data?.definition?.releaseNotes) && (
        <DeviceAppVersionCard title={t('input.releaseNotes.label', { suffix: '' })}>
          <MarkDownEditorCtrl content={data.definition.releaseNotes} mode={EditorMode.Preview} />
        </DeviceAppVersionCard>
      )}
      <DeviceAppVersionSummaryFileListCard
        chipInfo={applicationFilesList.length > 0 ? `${applicationFilesList.length}` : undefined}
        mainFiles={applicationFilesList}
        secondaryFiles={existingAppFilesList}
        sumUnitFile={getSumOfFileSizes(
          toArray(data?.build?.applicationFiles),
          t,
          toArray(data?.build?.existingAppFiles),
          applicationFiles,
        )}
        title={t('section.systemPackageVersionFiles')}
      />
      {shouldDisplayConfigurationCard && (
        <DeviceAppVersionSummaryFileListCard
          chipInfo={parameterTemplatesList.length > 0 ? `${parameterTemplatesList.length}` : undefined}
          isLoadingSecondary={isLoadingTemplateResources}
          mainFiles={parameterTemplatesList}
          secondaryFiles={existingTemplatesList}
          sumUnitFile={getSumOfFileSizes(
            toArray(data?.configure?.parameterTemplates),
            t,
            toArray(data?.configure?.existingTemplates),
            existingTemplates,
          )}
          title={t('title.configureSection')}
        />
      )}
    </div>
  );
}
